/* Built In Imports */
import { useRouter } from 'next/router';
import {
    useContext,
    useEffect
} from 'react';

/* External Imports */
import {
    Box
} from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import Breadcrum from '@components/Navigation/Breadcrum';
import ReportView from '@components/UI/ReportView';
import HeadComponent from '@components/Utility/HeadComponent';
import Loader from '@components/Utility/Loader';
import config from '@config';
import WrapLayout from '@Layout/WrapLayout';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@components/Utility/Shared/SharedService';
import {
    fetchFooterData, fetchHeaderMenuData, fetchHeaderTopMenuData
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/othersService';

/* Styles */

/**
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Page Data
 * @returns {ReactElement} IshaFoundationReports component
 */
const IshaFoundationReports = ({ region, language, pageData }) => {
  const authContext = useContext(AuthContext);
  const router = useRouter();
  const { query, isReady } = router;
  const { id } = query;

  useEffect(() => {
    authContext.RegionAndLang(region, language);
  }, [authContext, region, language]);

  if (!isReady) {
    return <Loader pos="relative" />;
  }

  if (!id) {
    return (
      <Box as="section" className="page-wrap container-article">
        <Box>
          <Box
            w={{ md: '632px', sm: '432px', base: '302px' }}
            minW="302px"
            maxW="632px"
            m="0 auto"
            d="grid"
            placeItems="center"
            // bg="#f4f4f4"
            p={{
              md: '51px 69px 57px 70px',
              sm: '45px 15px 54px',
              base: '45px 15px 54px',
            }}
            textAlign="center"
          >
            <Box
              as="p"
              pb="20px"
              fontSize={{ md: '18px', sm: '16px', base: '16px' }}
              fontFamily={'FedraSansStd-medium'}
              lineHeight={{ md: '1.56', sm: '1.5', base: '1.5' }}
              textAlign="center"
              color={'#464038'}
              className="userMessage"
            >
              <style global jsx>
                {`
                  .userMessage p {
                    line-height: 1.56;
                    letter-spacing: normal;
                    text-align: center;
                    color: #cf4520;
                  }
                `}
              </style>
              Oops! Something went wrong! This report is not found in our
              system. Help us improve your experience by sending an error
              report.
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <WrapLayout>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData?.availableLocales}
      >
        {' '}
      </HeadComponent>
      <Breadcrum
        page={pageData?.breadcrumbs}
        alsoin={pageData?.alsoin}
        url=""
        region={region}
        lang={language}
        pageConfig={pageData?.pageConfig}
      />
      <Box as="section" className="page-wrap container-article" py="20px">
        {/* <OthersCommonUI
          pageBody={pageData.body}
          region={region}
          lang={language}
        /> */}
        {/* <MidContainer w={768}> */}
        <ReportView id={id} />
        {/* </MidContainer> */}
      </Box>
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const reportsViewPaths = config.USE_LOCAL_URL
    ? [`/in/en/isha-foundation/reports/view`]
    : await getLandingPaths('L2', '/isha-foundation/reports/view');
  return {
    paths: reportsViewPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, language },
  req,
}) => {
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale(region, language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, language),
    'isomainmenu'
  );
  const footerData = await fetchFooterData(
    getLocale(region, language),
    'isofooter'
  );
  const pageData = await fetchDetails(
    region,
    language,
    `isha-foundation/reports/view`,
    { preview, data: previewData }
  );

  return {
    props: {
      /* Fetching the data from the API. */
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: region,
      language: language,
      footerData: footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false
    }
  };
};

export default IshaFoundationReports;
